// INTELIMOVE-XPR-001..004 (IM-ADR-015) — Experiência orientada a objetivos. Contrato da chave, catálogo dos cinco objetivos
// e o agrupamento das etapas das jornadas em MACROFASES visuais. Puro: sem banco, sem rede, sem IA, sem relógio.
// Princípios:
//   • As etapas das jornadas (J2, J4) continuam existindo no domínio, com o mesmo significado histórico. A macrofase só
//     AGRUPA etapas já derivadas pelo motor (`deriveSteps`) para a tela: nenhuma regra de etapa é refeita aqui.
//   • O estado da macrofase sai dos estados das etapas e da próxima ação do próprio motor; nunca de percentual.
//   • IM-JRN-015/016: planejar e acompanhar o plano são conduzidos pelo módulo de planejamento (modo PLANO): as fases
//     abaixo são as telas do fluxo, com estado vindo dos dados reais (base, plano, versões, realizado).
import type { JourneyType, StepId, StepState, StepReason } from './journeys.js';

/** IM-API-327 `GET /api/v1/experience`: chaves que a interface precisa conhecer para escolher a experiência. */
export interface ExperienceDto { v2: boolean; journeys: boolean; opportunities: boolean; decisionBrief: boolean; opportunityRadar: boolean }

export const OBJECTIVE_KEYS = ['planejar', 'concorrentes', 'empresas', 'ofertas', 'acompanhar-plano'] as const;
export type ObjectiveKey = (typeof OBJECTIVE_KEYS)[number];

/** Uma macrofase: um bloco compreensível do processo, com as etapas do motor que ela agrupa (vazio = conceitual). */
export interface MacroPhaseDef {
  readonly key: string;
  readonly name: string;
  /** Pergunta curta que a fase responde. */
  readonly question: string;
  /** O que a pessoa recebe ao concluir a fase. */
  readonly result: string;
  readonly steps: readonly StepId[];
}

/**
 * Como o objetivo é conduzido hoje: `JORNADA` (motor de jornada publicado), `CAPACIDADES` (módulos existentes, sem jornada
 * publicada — leitura de apoio) ou `PLANO` (IM-JRN-015/016: planejamento estratégico e acompanhamento do plano).
 */
export type ObjectiveMode = 'JORNADA' | 'CAPACIDADES' | 'PLANO';
export interface ObjectiveDef {
  readonly key: ObjectiveKey;
  readonly title: string;
  /** Resultado esperado, em uma frase. */
  readonly outcome: string;
  readonly journeyType: JourneyType;
  readonly mode: ObjectiveMode;
  readonly phases: readonly MacroPhaseDef[];
}

const p = (key: string, name: string, question: string, result: string, steps: StepId[] = []): MacroPhaseDef => ({ key, name, question, result, steps });

/** Conhecer meus concorrentes: as 16 etapas da J2 em cinco macrofases (mapeamento explícito, testado). */
export const J2_MACROPHASES: readonly MacroPhaseDef[] = [
  p('encontrar', 'Encontrar', 'Quem disputa seus clientes?', 'Empresas encontradas, com identidade conferida.', ['J2-01', 'J2-02', 'J2-03']),
  p('confirmar', 'Confirmar', 'Qual é o papel de cada empresa?', 'Cada empresa classificada por você.', ['J2-04']),
  p('conhecer', 'Conhecer', 'Como cada um compete?', 'Uma síntese por concorrente, com evidências.', ['J2-05', 'J2-06', 'J2-08']),
  p('comparar', 'Comparar', 'Onde você ganha e onde perde?', 'Papel de cada concorrente e espaços para se diferenciar.', ['J2-07']),
  p('acompanhar', 'Acompanhar', 'O que mudou e o que fazer?', 'Mudanças que importam, com resposta recomendada.', ['J2-09', 'J2-10', 'J2-11', 'J2-12', 'J2-13', 'J2-14', 'J2-15', 'J2-16']),
];

/** Rever minhas ofertas: as 12 etapas da J4 em cinco macrofases. */
export const J4_MACROPHASES: readonly MacroPhaseDef[] = [
  p('mapear', 'Mapear', 'O que você vende e para quem?', 'Ofertas ligadas a perfis, segmentos e necessidades.', ['J4-01', 'J4-02']),
  p('cobrir', 'Cobrir', 'Onde há lacunas ou sobreposição?', 'Segmentos sem oferta adequada e ofertas que se sobrepõem.', ['J4-04', 'J4-06']),
  p('comparar', 'Comparar', 'Como você se compara?', 'Concorrentes, substitutos, preços comparáveis e diferenciais.', ['J4-03', 'J4-05']),
  p('priorizar', 'Priorizar', 'O que fortalecer, combinar ou retirar?', 'Recomendação por oferta, com a capacidade de entrega considerada.', ['J4-07', 'J4-08', 'J4-09', 'J4-10']),
  p('decidir', 'Decidir', 'O que você vai fazer?', 'Decisão registrada, com responsável, prazo e acompanhamento.', ['J4-11', 'J4-12']),
];

/** IM-JRN-015 (IM-UX-145): Base atual → Direção → Cenários → Metas → Plano aprovado. */
export const PLAN_PHASES: readonly MacroPhaseDef[] = [
  p('base', 'Base atual', 'De onde você parte?', 'Faturamento, despesas, ofertas e mercado consolidados.'),
  p('direcao', 'Direção', 'Para onde ir?', 'Objetivo, crescimento esperado, ofertas e segmentos prioritários.'),
  p('cenarios', 'Cenários', 'Quanto é possível?', 'Três cenários comparáveis e o recomendado, com as premissas à vista.'),
  p('metas', 'Metas', 'Quanto e quando?', 'Meta anual distribuída por mês, por oferta e metas comerciais.'),
  p('plano', 'Plano aprovado', 'Como chegar lá?', 'Plano executivo aprovado e versionado.'),
];
/** IM-JRN-016 (IM-UX-146): Atualizar realizado → Comparar → Entender desvios → Corrigir rota. */
export const TRACK_PHASES: readonly MacroPhaseDef[] = [
  p('realizado', 'Atualizar realizado', 'O que aconteceu?', 'Realizado do mês registrado.'),
  p('comparar', 'Comparar', 'Estamos no ritmo?', 'Previsto × realizado, acumulado e projeção de fechamento.'),
  p('desvios', 'Entender desvios', 'Por que nos afastamos?', 'Fatos observados separados das hipóteses.'),
  p('corrigir', 'Corrigir rota', 'O que fazer agora?', 'Revisão do mês, ações e, se preciso, nova versão do plano.'),
];

export const OBJECTIVES: readonly ObjectiveDef[] = [
  { key: 'planejar', title: 'Planejar meu próximo ano', outcome: 'Prioridades, metas e um plano comercial executável.', journeyType: 'PLANEJAMENTO_ESTRATEGICO', mode: 'PLANO', phases: PLAN_PHASES },
  { key: 'concorrentes', title: 'Conhecer meus concorrentes', outcome: 'Saber quem disputa seus clientes, como competem e o que mudou.', journeyType: 'CONCORRENTES', mode: 'JORNADA', phases: J2_MACROPHASES },
  { key: 'empresas', title: 'Encontrar empresas para vender', outcome: 'Uma carteira de empresas com aderência e as pessoas certas.', journeyType: 'CARTEIRA_ALVOS', mode: 'CAPACIDADES',
    phases: [p('perfil', 'Perfil', 'Quem é o cliente ideal?', 'Perfis de cliente confirmados.'), p('encontrar', 'Encontrar', 'Quais empresas se encaixam?', 'Empresas sugeridas com evidência.'),
      p('avaliar', 'Avaliar', 'Vale a pena abordar?', 'Empresas aprovadas ou descartadas por você.'), p('pessoas', 'Pessoas', 'Com quem falar?', 'Decisores e influenciadores conferidos.'),
      p('carteira', 'Carteira', 'Quem acompanhar?', 'Carteira de empresas em acompanhamento.')] },
  { key: 'ofertas', title: 'Rever minhas ofertas', outcome: 'Saber o que fortalecer, combinar, reposicionar, desenvolver ou retirar.', journeyType: 'PORTFOLIO_POSICIONAMENTO', mode: 'JORNADA', phases: J4_MACROPHASES },
  { key: 'acompanhar-plano', title: 'Acompanhar meu plano', outcome: 'Comparar metas e realizado, entender desvios e corrigir a rota.', journeyType: 'ACOMPANHAMENTO_PLANO', mode: 'PLANO', phases: TRACK_PHASES },
];
export const objectiveOf = (key: string): ObjectiveDef | null => OBJECTIVES.find((o) => o.key === key) ?? null;

// ── Estado da macrofase ──────────────────────────────────────────────────────────────────────────────────────────
/**
 * Estados da macrofase (nunca só cor: cada um tem rótulo). `AGUARDANDO_FASE_ANTERIOR` = ainda depende de uma fase
 * anterior (não é bloqueio por problema); `BLOQUEADA` = há um impedimento real (execução que falhou, leitura indisponível,
 * capacidade inexistente); `CONTINUA` = conduzida e agora em acompanhamento contínuo.
 */
export const MACRO_STATES = [
  'CONCLUIDA', 'ATUAL', 'PRONTA', 'EM_ANDAMENTO', 'AGUARDANDO_DECISAO', 'AGUARDANDO_REVISAO', 'AGUARDANDO_INFORMACAO', 'BLOQUEADA',
  'AGUARDANDO_FASE_ANTERIOR', 'CONTINUA', 'DISPENSADA',
] as const;
export type MacroState = (typeof MACRO_STATES)[number];
export const MACRO_STATE_LABEL: Record<MacroState, string> = {
  CONCLUIDA: 'Concluída', ATUAL: 'Em andamento', PRONTA: 'Pronta para começar', EM_ANDAMENTO: 'O InteliMove está trabalhando', AGUARDANDO_DECISAO: 'Aguardando sua decisão',
  AGUARDANDO_REVISAO: 'Aguardando sua revisão', AGUARDANDO_INFORMACAO: 'Aguardando informação', BLOQUEADA: 'Bloqueada', AGUARDANDO_FASE_ANTERIOR: 'Depois',
  CONTINUA: 'Contínua', DISPENSADA: 'Dispensada',
};

/** O mínimo de uma etapa derivada que a macrofase precisa (subconjunto de `JourneyStepViewDto`). */
export interface MacroStepInput {
  stepId: StepId; state: StepState; reason: StepReason; kind: 'OBRIGATORIA' | 'OPCIONAL' | 'CONDICIONAL'; phase: 'CONDUCAO' | 'ACOMPANHAMENTO';
  pendingReview: number; openDecisions: number;
}
export interface MacroPhaseView {
  key: string; name: string; question: string; result: string;
  state: MacroState;
  /** É a fase da próxima ação do motor (ou, sem próxima ação, a primeira ainda aberta). */
  current: boolean;
  /** Etapas essenciais (obrigatórias e condicionais que se aplicam, de condução) concluídas × aplicáveis. */
  essentialDone: number; essentialTotal: number;
  /** Decisões e revisões abertas nas etapas da fase. */
  decisions: number; reviews: number;
  /** Etapas concluídas mas fora do limite de atualidade. */
  stale: number;
  steps: readonly StepId[];
}

const DONE: ReadonlySet<StepState> = new Set(['CONCLUIDA', 'CONCLUIDA_COM_LIMITACOES', 'DESATUALIZADA']);
const REAL_BLOCK: ReadonlySet<StepReason> = new Set(['EXECUCAO_FALHOU', 'LEITURA_INDISPONIVEL', 'CAPACIDADE_INDISPONIVEL']);

/** Estado de UMA macrofase a partir das etapas já derivadas pelo motor. */
export function macroStateOf(steps: readonly MacroStepInput[]): MacroState {
  const applicable = steps.filter((s) => s.state !== 'DISPENSADA');
  if (!applicable.length) return steps.length ? 'DISPENSADA' : 'AGUARDANDO_FASE_ANTERIOR';
  const essential = applicable.filter((s) => s.kind !== 'OPCIONAL' && s.phase === 'CONDUCAO');
  const open = essential.filter((s) => !DONE.has(s.state));
  if (!open.length) {
    // Opcional ainda aberta não segura a fase; acompanhamento vivo a torna contínua.
    return applicable.some((s) => s.phase === 'ACOMPANHAMENTO') ? 'CONTINUA' : 'CONCLUIDA';
  }
  if (open.some((s) => s.state === 'AGUARDANDO_DECISAO')) return 'AGUARDANDO_DECISAO';
  if (open.some((s) => s.state === 'AGUARDANDO_REVISAO')) return 'AGUARDANDO_REVISAO';
  if (open.some((s) => s.state === 'AGUARDANDO_DADO')) return 'AGUARDANDO_INFORMACAO';
  if (open.some((s) => s.state === 'EM_ANDAMENTO')) return 'EM_ANDAMENTO';
  if (open.some((s) => s.state === 'BLOQUEADA' && REAL_BLOCK.has(s.reason))) return 'BLOQUEADA';
  if (open.some((s) => s.state === 'PRONTA_PARA_COMECAR')) return 'PRONTA';
  return 'AGUARDANDO_FASE_ANTERIOR';
}

const SETTLED: ReadonlySet<MacroState> = new Set(['CONCLUIDA', 'CONTINUA', 'DISPENSADA']);

/**
 * Agrupa as etapas derivadas nas macrofases. `nextStepId` é a etapa da próxima ação do motor (`nextAction.stepId`).
 * Etapa da definição ausente em `steps` é ignorada (definição antiga); etapa presente fora do mapeamento é erro do
 * mapeamento (o teste garante cobertura total das definições publicadas).
 */
export function deriveMacroPhases(phases: readonly MacroPhaseDef[], steps: readonly MacroStepInput[], nextStepId: StepId | null): MacroPhaseView[] {
  const byId = new Map(steps.map((s) => [s.stepId, s] as const));
  const views: MacroPhaseView[] = phases.map((ph) => {
    const own = ph.steps.map((id) => byId.get(id)).filter((x): x is MacroStepInput => !!x);
    const essential = own.filter((s) => s.state !== 'DISPENSADA' && s.kind !== 'OPCIONAL' && s.phase === 'CONDUCAO');
    return {
      key: ph.key, name: ph.name, question: ph.question, result: ph.result, steps: ph.steps,
      state: macroStateOf(own), current: false,
      essentialDone: essential.filter((s) => DONE.has(s.state)).length, essentialTotal: essential.length,
      decisions: own.filter((s) => s.state !== 'DISPENSADA').reduce((n, s) => n + s.openDecisions, 0),
      reviews: own.filter((s) => s.state !== 'DISPENSADA').reduce((n, s) => n + s.pendingReview, 0),
      stale: own.filter((s) => s.state === 'DESATUALIZADA').length,
    };
  });
  let idx = nextStepId ? views.findIndex((v) => v.steps.includes(nextStepId)) : -1;
  if (idx < 0) idx = views.findIndex((v) => !SETTLED.has(v.state));
  if (idx >= 0) {
    const v = views[idx]!;
    v.current = true;
    // A fase da próxima ação nunca aparece como "depois": ela é onde a pessoa está.
    if (v.state === 'AGUARDANDO_FASE_ANTERIOR' || v.state === 'PRONTA') v.state = 'ATUAL';
  }
  return views;
}

/** Posição legível ("fase 2 de 5") e fases concluídas — sem percentual (IM-UX-110). */
export function macroProgress(views: readonly MacroPhaseView[]): { position: number | null; total: number; settled: number } {
  const i = views.findIndex((v) => v.current);
  return { position: i >= 0 ? i + 1 : null, total: views.length, settled: views.filter((v) => SETTLED.has(v.state)).length };
}

/** Situação do objetivo no catálogo (linguagem de tela). */
export const OBJECTIVE_STATUSES = ['COMECAR', 'CONTINUAR', 'PRONTO', 'AGUARDANDO_INFORMACAO', 'AGUARDANDO_PLANO', 'EM_PREPARACAO'] as const;
export type ObjectiveStatus = (typeof OBJECTIVE_STATUSES)[number];
export const OBJECTIVE_STATUS_LABEL: Record<ObjectiveStatus, string> = {
  COMECAR: 'Começar', CONTINUAR: 'Continuar', PRONTO: 'Pronto', AGUARDANDO_INFORMACAO: 'Aguardando informação', AGUARDANDO_PLANO: 'Aguardando plano', EM_PREPARACAO: 'Em preparação',
};

// ── IM-XPR-005 (IM-UX-120): estado PADRONIZADO da fase na tela ─────────────────────────────────────────────────────────
/**
 * Seis estados de tela, sempre com rótulo: Não iniciada, Disponível, Em andamento, Aguardando decisão, Bloqueada e
 * Concluída. Saem do estado da macrofase (que continua derivado só do motor). Fase que ainda depende de outra NUNCA aparece
 * como "pronta para começar": é "Bloqueada", dizendo por quê, o que falta, a ação para resolver e o que será liberado.
 */
export const PHASE_STATUSES = ['NAO_INICIADA', 'DISPONIVEL', 'EM_ANDAMENTO', 'AGUARDANDO_DECISAO', 'BLOQUEADA', 'CONCLUIDA'] as const;
export type PhaseStatus = (typeof PHASE_STATUSES)[number];
export const PHASE_STATUS_LABEL: Record<PhaseStatus, string> = {
  NAO_INICIADA: 'Não iniciada', DISPONIVEL: 'Disponível', EM_ANDAMENTO: 'Em andamento', AGUARDANDO_DECISAO: 'Aguardando decisão', BLOQUEADA: 'Bloqueada', CONCLUIDA: 'Concluída',
};
export interface PhaseBlock { why: string; waitingFor: string | null; action: string; unlocks: string }
export interface PhaseStatusView { status: PhaseStatus; label: string; detail: string | null; block: PhaseBlock | null }

const MACRO_TO_PHASE: Record<MacroState, PhaseStatus> = {
  CONCLUIDA: 'CONCLUIDA', CONTINUA: 'CONCLUIDA', DISPENSADA: 'CONCLUIDA', AGUARDANDO_DECISAO: 'AGUARDANDO_DECISAO', AGUARDANDO_REVISAO: 'AGUARDANDO_DECISAO',
  ATUAL: 'EM_ANDAMENTO', EM_ANDAMENTO: 'EM_ANDAMENTO', AGUARDANDO_INFORMACAO: 'EM_ANDAMENTO', PRONTA: 'DISPONIVEL', BLOQUEADA: 'BLOQUEADA', AGUARDANDO_FASE_ANTERIOR: 'BLOQUEADA',
};

/**
 * Estado de tela de UMA fase. `deps` = dependências das etapas da definição publicada (id → dependsOn). Sem instância
 * (objetivo não iniciado) = "Não iniciada".
 */
export function phaseStatusOf(view: MacroPhaseView | null | undefined, all: readonly MacroPhaseView[] | null, deps: Readonly<Record<string, readonly string[]>>): PhaseStatusView {
  if (!view || !all) return { status: 'NAO_INICIADA', label: PHASE_STATUS_LABEL.NAO_INICIADA, detail: null, block: null };
  const status = MACRO_TO_PHASE[view.state];
  const detail = view.state === 'CONTINUA' ? 'em acompanhamento contínuo' : view.state === 'DISPENSADA' ? 'dispensada' : view.state === 'EM_ANDAMENTO' ? 'o InteliMove está trabalhando'
    : view.state === 'AGUARDANDO_INFORMACAO' ? 'aguardando informação' : view.state === 'AGUARDANDO_REVISAO' ? 'aguardando sua revisão' : null;
  if (status !== 'BLOQUEADA') return { status, label: PHASE_STATUS_LABEL[status], detail, block: null };
  if (view.state === 'BLOQUEADA') {
    return { status, label: PHASE_STATUS_LABEL.BLOQUEADA, detail: null,
      block: { why: 'Uma leitura ou execução necessária falhou ou não está disponível.', waitingFor: null, action: 'Abra as etapas detalhadas para ver o que falhou e tentar de novo.', unlocks: view.result } };
  }
  // Dependência: as fases donas das etapas de que esta fase depende e que ainda não terminaram.
  const settled = new Set(['CONCLUIDA', 'CONTINUA', 'DISPENSADA']);
  const ownerOf = (stepId: string) => all.find((p) => p.steps.includes(stepId as StepId));
  const waiting = [...new Set(view.steps.flatMap((s) => deps[s] ?? []).map(ownerOf).filter((p): p is MacroPhaseView => !!p && p.key !== view.key && !settled.has(p.state)))];
  const first = waiting[0] ?? all.slice(0, all.indexOf(view)).find((p) => !settled.has(p.state)) ?? null;
  return { status, label: PHASE_STATUS_LABEL.BLOQUEADA, detail: null, block: {
    why: first ? `Depende da fase ${first.name}, que ainda não foi concluída.` : 'Depende de uma fase anterior que ainda não foi concluída.',
    waitingFor: first?.name ?? null,
    action: first ? `Conclua a fase ${first.name}: ${first.question.charAt(0).toLowerCase()}${first.question.slice(1)}` : 'Conclua a fase anterior.',
    unlocks: `Ao concluir, esta fase entrega: ${view.result.charAt(0).toLowerCase()}${view.result.slice(1)}`,
  } };
}
/** Dependências id → dependsOn de uma definição publicada (forma mínima para `phaseStatusOf`). */
export const stepDeps = (def: { steps: ReadonlyArray<{ id: string; dependsOn: readonly string[] }> }): Record<string, readonly string[]> => Object.fromEntries(def.steps.map((s) => [s.id, s.dependsOn]));
